import {
    toggleEmpty, toggleFocused, togglePristine, toggleTouched, toggleValid,
    TToggleFormClassApply, CLASSNAME_NOT_VALID, CLASSNAME_IS_AUTOFILL,
} from "./toggle-form-class";

import {
    EVENT_BLUR, EVENT_FOCUS, EVENT_INPUT, EVENT_ANIMATION_START,
} from "./form-event";

enum TextBoxCSSAnimations {
    onAutoFillStart = "onAutoFillStart",
    onAutoFillCancel = "onAutoFillCancel",
}

interface ITextBoxContainerElement extends HTMLElement {
    dataset: {};
}

/**
 * Form Control for Text-like input
 *
 * Generic base control for anything that walks and talks like a text input.
 * This includes plain old text, email, password, number, telephone, etc.
 *
 * @todo Add validators
 */
export class TextBox {
    public static SELECTOR_INPUT: string = "input";
    public static SELECTOR_LABEL: string = "label";
    public static SELECTOR_BOTTOM_LINE: string = "[class*='__bottom-line']";
    public static SELECTOR_LETTER_COUNT: string = "[class*='__letter-count']";
    public static SELECTOR_LETTER_COUNT_NUM_CHARS: string = "[class*='__letter-count__num-chars']";
    public static SELECTOR_LETTER_COUNT_MAX_CHARS: string = "[class*='__letter-count__max-chars']";

    protected inputElement: HTMLInputElement | HTMLSelectElement;

    private containerElement: ITextBoxContainerElement;
    private labelElement: HTMLLabelElement;
    private bottomLineElement: HTMLElement;

    private usesLetterCount: boolean;
    private letterCountNumCharsElement: HTMLElement;
    private letterCountMaxCharsElement: HTMLElement;

    private $$isTouched: boolean;
    private $$isPristine: boolean;
    private $$isEmpty: boolean;
    private $$isFocused: boolean;
    private $$isValid: boolean;

    private $onFocus: EventListener;
    private $onBlur: EventListener;
    private $onInput: EventListener;
    private $onAnimationStart: EventListener;

    private togglePristine: TToggleFormClassApply;
    private toggleTouched: TToggleFormClassApply;
    private toggleEmpty: TToggleFormClassApply;
    private toggleFocused: TToggleFormClassApply;
    private toggleValid: TToggleFormClassApply;

    constructor(
        containerElement: Element,
        inputElement = containerElement.querySelector(TextBox.SELECTOR_INPUT),
        labelElement = containerElement.querySelector(TextBox.SELECTOR_LABEL),
        bottomLineElement = containerElement.querySelector(TextBox.SELECTOR_BOTTOM_LINE),
    ) {

        this.containerElement = containerElement as ITextBoxContainerElement;

        if (inputElement instanceof HTMLInputElement || inputElement instanceof HTMLSelectElement) {
            this.inputElement = inputElement;
        } else {
            throw new Error(`Expected an "input" element but none was found`);
        }

        if (labelElement instanceof HTMLLabelElement) {
            this.labelElement = labelElement;
        } else {
            throw new Error(`Expected a "label" element but none was found`);
        }

        if (bottomLineElement instanceof HTMLElement) {
            this.bottomLineElement = bottomLineElement;
        } else {
            throw new Error(`Expected a "*__bottom-line" element but none was found`);
        }

        if (this.inputElement.hasAttribute("maxlength")) {
            this.letterCountNumCharsElement =
                containerElement.querySelector(TextBox.SELECTOR_LETTER_COUNT_NUM_CHARS) as HTMLElement;
            this.letterCountMaxCharsElement =
                containerElement.querySelector(TextBox.SELECTOR_LETTER_COUNT_MAX_CHARS) as HTMLElement;
            this.usesLetterCount = true;
        }

        if (this.containerElement.classList.contains(CLASSNAME_NOT_VALID)) {
            this.$$isValid = false;
        }

        this.togglePristine = togglePristine(this.containerElement);
        this.toggleTouched = toggleTouched(this.containerElement);
        this.toggleEmpty = toggleEmpty(this.containerElement);
        this.toggleFocused = toggleFocused(this.containerElement);
        this.toggleValid = toggleValid(this.containerElement);

        this.init();
    }

    public get value(): string {
        return this.inputElement.value;
    }

    public set value(val: string) {
        this.inputElement.value = val;
    }

    public get isEmpty(): boolean {
        return !this.value;
    }

    public get notEmpty(): boolean {
        return !!this.value;
    }

    public get currentLength(): number {
        return this.inputElement.value.length;
    }

    public get maxLength(): string | null {
        return this.inputElement.getAttribute("maxlength");
    }

    private onFocus(event: FocusEvent): void {
        if (this.$$isTouched === false) {
            this.$$isTouched = true;
            this.toggleTouched(this.$$isTouched);
        }
        this.$$isFocused = true;
        this.toggleFocused(this.$$isFocused);
    }

    private onBlur(event: FocusEvent): void {
        this.$$isFocused = false;
        this.toggleFocused(this.$$isFocused);
    }

    private onInput(event: Event): void {
        if (this.$$isPristine === false) {
            this.$$isPristine = true;
            this.togglePristine(this.$$isPristine);
        }
        if (this.$$isValid === false) {
            this.$$isValid = true;
            this.toggleValid(this.$$isValid);
        }

        this.updateLetterCount();
        this.toggleEmpty(this.isEmpty);

        // tslint:disable-next-line:no-console
        console.debug("TextBox:", "onInput:", event, this);
    }

    /**
     * Capture specific CSS Animation events
     * @param event
     */
    private onAnimationStart(event: AnimationEvent): void {
        // tslint:disable-next-line:no-console
        console.debug("TextBox:", "onAnimationStart()", "animation:", event.animationName, this);
        switch (event.animationName) {
            case TextBoxCSSAnimations.onAutoFillStart:
                return this.onAutoFillStart();
            case TextBoxCSSAnimations.onAutoFillCancel:
                return this.onAutoFillCancel();
        }
    }

    /**
     * Re-trigger validation on browser autofill
     */
    private onAutoFillStart(): void {
        // tslint:disable-next-line:no-console
        console.debug("TextBox:", "onAutoFillStart()", this);
        this.toggleEmpty(this.isEmpty);
        this.containerElement.classList.toggle(CLASSNAME_IS_AUTOFILL, true);
    }

    /**
     * Re-trigger validation on browser autofill cancel
     */
    private onAutoFillCancel(): void {
        // tslint:disable-next-line:no-console
        console.debug("TextBox:", "onAutoFillCancel()", this);
        this.toggleEmpty(this.isEmpty);
        this.containerElement.classList.toggle(CLASSNAME_IS_AUTOFILL, false);
    }

    private bindListeners(): void {
        this.$onFocus = this.onFocus.bind(this);
        this.$onBlur = this.onBlur.bind(this);
        this.$onInput = this.onInput.bind(this);
        this.$onAnimationStart = this.onAnimationStart.bind(this);
    }

    private addListeners(): void {
        this.inputElement.addEventListener(EVENT_FOCUS, this.$onFocus);
        this.inputElement.addEventListener(EVENT_BLUR, this.$onBlur);
        this.inputElement.addEventListener(EVENT_INPUT, this.$onInput);
        this.inputElement.addEventListener(EVENT_ANIMATION_START, this.$onAnimationStart);
    }

    private removeListeners(): void {
        this.inputElement.removeEventListener(EVENT_FOCUS, this.$onFocus);
        this.inputElement.removeEventListener(EVENT_BLUR, this.$onBlur);
        this.inputElement.removeEventListener(EVENT_INPUT, this.$onInput);
        this.inputElement.removeEventListener(EVENT_ANIMATION_START, this.$onAnimationStart);
    }

    private setInitialState(): void {
        const isEmpty = this.isEmpty;
        const notEmpty = this.notEmpty;

        this.toggleEmpty(isEmpty);
        this.togglePristine(notEmpty);

        this.updateLetterCount();
    }

    private updateLetterCount(): void {
        if (!this.usesLetterCount) {
            return;
        }
        if (this.letterCountNumCharsElement instanceof HTMLElement) {
            this.letterCountNumCharsElement.dataset.numChars = String(this.currentLength);
        }
        if (this.letterCountMaxCharsElement instanceof HTMLElement) {
            this.letterCountMaxCharsElement.dataset.maxChars = String(this.maxLength);
        }
    }

    private init(): void {
        this.bindListeners();
        this.addListeners();
        this.setInitialState();

        // tslint:disable-next-line:no-console
        console.debug("TextBox:", "init:", this);
    }
}



// WEBPACK FOOTER //
// ../common/material/form/text-box.ts